import { useEffect, useState } from "react";
import { ILocationProps } from "../services/Location/types";

const WS_RECONNECT_BASE_MS = 2000;
const WS_RECONNECT_MAX_MS = 30000;
const WS_RECONNECT_HIDDEN_MS = 12000;

export function useLocationStream(url: string) {
    const [locations, setLocations] = useState<ILocationProps[]>([]);

    useEffect(() => {
        if (!url) return;

        let ws: WebSocket | null = null;
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        let isActive = true;
        let reconnectAttempts = 0;

        const normalizeLocation = (item: any): ILocationProps | null => {
            if (!item || typeof item.entityId !== "string" || item.entityId.trim().length === 0) {
                return null;
            }

            const hasValidLatitude = typeof item.latitude === "number" && Number.isFinite(item.latitude);
            const hasValidLongitude = typeof item.longitude === "number" && Number.isFinite(item.longitude);

            return {
                ...item,
                hasLocation: typeof item.hasLocation === "boolean"
                    ? item.hasLocation
                    : hasValidLatitude && hasValidLongitude,
            } as ILocationProps;
        };

        const mergeLocations = (prev: ILocationProps[], incomingItems: ILocationProps[]) => {
            if (incomingItems.length === 0) return prev;

            const map = new Map(prev.map((item) => [item.entityId, item]));
            let changed = false;

            incomingItems.forEach((incoming) => {
                const current = map.get(incoming.entityId);

                if (!current) {
                    map.set(incoming.entityId, incoming);
                    changed = true;
                    return;
                }

                const resolvedLatitude =
                    typeof incoming.latitude === "number" ? incoming.latitude : current.latitude;
                const resolvedLongitude =
                    typeof incoming.longitude === "number" ? incoming.longitude : current.longitude;
                const resolvedTimestamp = incoming.timestamp ?? current.timestamp;

                const merged: ILocationProps = {
                    ...current,
                    ...incoming,
                    latitude: resolvedLatitude,
                    longitude: resolvedLongitude,
                    timestamp: resolvedTimestamp,
                    hasLocation: typeof incoming.hasLocation === "boolean"
                        ? incoming.hasLocation
                        : !!(resolvedLatitude !== null && resolvedLongitude !== null),
                };

                const didChange =
                    current.latitude !== merged.latitude
                    || current.longitude !== merged.longitude
                    || current.timestamp !== merged.timestamp
                    || current.hasLocation !== merged.hasLocation
                    || current.speed !== merged.speed
                    || current.heading !== merged.heading
                    || current.altitude !== merged.altitude
                    || current.source !== merged.source
                    || current.vehiclePlate !== merged.vehiclePlate
                    || current.modelName !== merged.modelName;

                if (didChange) {
                    map.set(incoming.entityId, merged);
                    changed = true;
                }
            });

            return changed ? Array.from(map.values()) : prev;
        };

        const connect = () => {
            if (!isActive) return;

            if (document.visibilityState === "hidden") {
                reconnectTimer = setTimeout(connect, WS_RECONNECT_HIDDEN_MS);
                return;
            }

            ws = new WebSocket(url);

            ws.onopen = () => {
                reconnectAttempts = 0;
            };

            ws.onmessage = (event) => {
                try {
                    const parsed = JSON.parse(event.data);
                    const data = parsed?.data ?? parsed;

                    if (Array.isArray(data)) {
                        const normalizedBatch = data
                            .map(normalizeLocation)
                            .filter((item): item is ILocationProps => item !== null);

                        setLocations((prev) => mergeLocations(prev, normalizedBatch));
                        return;
                    }

                    const normalized = normalizeLocation(data);
                    if (normalized) {
                        setLocations((prev) => mergeLocations(prev, [normalized]));
                    }
                } catch (err) {
                    console.error("Invalid WS payload", err);
                }
            };

            ws.onerror = () => {
                ws?.close();
            };

            ws.onclose = () => {
                if (!isActive) return;

                const backoff = Math.min(
                    WS_RECONNECT_MAX_MS,
                    WS_RECONNECT_BASE_MS * Math.pow(2, reconnectAttempts),
                );

                reconnectAttempts += 1;
                const jitter = Math.floor(Math.random() * 500);
                reconnectTimer = setTimeout(connect, backoff + jitter);
            };
        };

        const handleVisibilityChange = () => {
            if (!isActive || document.visibilityState !== "visible") {
                return;
            }

            if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
                return;
            }

            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
                reconnectTimer = null;
            }

            connect();
        };

        document.addEventListener("visibilitychange", handleVisibilityChange);

        connect();

        return () => {
            isActive = false;

            if (reconnectTimer) {
                clearTimeout(reconnectTimer);
            }

            if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
                ws.close();
            }

            document.removeEventListener("visibilitychange", handleVisibilityChange);
        };
    }, [url]);

    return locations;
}
